import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Filter, Image as ImageIcon, Star } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { resolveMediaUrl } from "@/pages/ImpactHub";

export default function AdminMediaInventory() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categoryFilter, setCategoryFilter] = useState("");
    const [sectionFilter, setSectionFilter] = useState("");
    const [q, setQ] = useState("");

    useEffect(() => {
        api.get("/admin/media/inventory")
            .then(({ data }) => setItems(data.items || []))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    const categories = useMemo(() => Array.from(new Set(items.map((i) => i.category).filter(Boolean))).sort(), [items]);
    const sections = useMemo(() => Array.from(new Set(items.map((i) => i.website_section).filter(Boolean))).sort(), [items]);

    const filtered = useMemo(() => items.filter((it) => {
        if (categoryFilter && it.category !== categoryFilter) return false;
        if (sectionFilter && it.website_section !== sectionFilter) return false;
        if (q) {
            const needle = q.toLowerCase();
            const hay = [it.filename, it.original_filename, it.alt_text, it.caption, it.project_title, it.programme].filter(Boolean).join(" ").toLowerCase();
            if (!hay.includes(needle)) return false;
        }
        return true;
    }), [items, categoryFilter, sectionFilter, q]);

    return (
        <div data-testid="admin-media-inventory">
            <div className="gsf-eyebrow">Impact Hub</div>
            <h1 className="mt-2 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold">Media Inventory</h1>
            <p className="mt-2 text-sm text-gray-600 max-w-2xl">
                Authentic photographs and videos currently attached to Foundation projects. Add or update media in the Projects editor.
            </p>

            <div className="mt-6 gsf-card p-4 flex flex-wrap gap-3 items-center" data-testid="media-inv-filters">
                <Filter size={16} className="text-gray-400" />
                <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} data-testid="filter-category"
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm">
                    <option value="">All categories</option>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={sectionFilter} onChange={(e) => setSectionFilter(e.target.value)} data-testid="filter-section"
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm">
                    <option value="">All sections</option>
                    {sections.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input type="search" placeholder="Search filename, caption, project…" value={q} onChange={(e) => setQ(e.target.value)}
                    data-testid="filter-search"
                    className="flex-1 min-w-[220px] rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
                <div className="text-xs text-gray-500 ml-auto" data-testid="inv-count">{filtered.length} of {items.length} items</div>
            </div>

            {loading ? (
                <p className="mt-6 text-gray-500 text-sm">Loading inventory…</p>
            ) : filtered.length === 0 ? (
                <div className="mt-6 gsf-card p-8 text-sm text-gray-500 text-center" data-testid="inv-empty">
                    <ImageIcon size={40} className="mx-auto text-gray-300 mb-3" />
                    No media matches the current filters.
                </div>
            ) : (
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="inv-grid">
                    {filtered.map((it, idx) => (
                        <article key={`${it.url}-${idx}`} className="gsf-card overflow-hidden flex flex-col" data-testid={`inv-item-${idx}`}>
                            <div className="aspect-video bg-[color:var(--gsf-soft-green)] relative">
                                {it.media_type?.startsWith("image") ? (
                                    <img src={resolveMediaUrl(it.url)} alt={it.alt_text || it.filename} className="w-full h-full object-cover" loading="lazy" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">{it.media_type}</div>
                                )}
                                {it.featured && (
                                    <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full bg-[color:var(--gsf-gold-500)] text-black font-semibold flex items-center gap-1"><Star size={10} /> Featured</span>
                                )}
                                {!it.published && (
                                    <span className="absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded-full bg-gray-800 text-white">Draft</span>
                                )}
                            </div>
                            <div className="p-4 flex-1 flex flex-col gap-1 text-xs">
                                <div className="text-[10px] uppercase tracking-widest text-[color:var(--gsf-green-600)] font-semibold">{it.category || "Uncategorised"}</div>
                                <div className="font-medium text-sm text-[color:var(--gsf-green-700)] truncate" title={it.original_filename}>{it.original_filename || it.filename}</div>
                                {it.caption && <div className="text-gray-600 italic line-clamp-2">"{it.caption}"</div>}
                                <dl className="mt-1 grid grid-cols-2 gap-x-2 gap-y-0.5 text-gray-500">
                                    <dt className="font-medium text-gray-400">Programme</dt><dd className="truncate">{it.programme || "—"}</dd>
                                    <dt className="font-medium text-gray-400">Section</dt><dd className="truncate">{it.website_section || "—"}</dd>
                                    <dt className="font-medium text-gray-400">Project</dt><dd className="truncate">{it.project_title || "Unattached"}</dd>
                                    <dt className="font-medium text-gray-400">Type</dt><dd className="truncate">{it.media_type}</dd>
                                    {it.size_bytes > 0 && (<><dt className="font-medium text-gray-400">Size</dt><dd>{(it.size_bytes / 1024).toFixed(0)} KB</dd></>)}
                                    <dt className="font-medium text-gray-400">Uploaded</dt><dd className="truncate">{(it.uploaded_at || "").slice(0, 10)}</dd>
                                </dl>
                                {it.alt_text && <div className="mt-2 text-[10px] text-gray-500 bg-gray-50 rounded p-2"><span className="font-semibold">Alt:</span> {it.alt_text}</div>}
                            </div>
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
}
